/**
 * Notification helpers
 *
 * Centralises all in-app notification inserts so action hooks don't repeat
 * boilerplate. Each helper is fire-and-forget — callers should `.catch(warn)`.
 *
 * Delivery is intentionally silent: errors are logged but never re-thrown so
 * a notification failure never blocks the primary action (booking, approval…).
 */

import { supabase } from "@/lib/supabase";
import type { NotificationType } from "@/types/database";

// ─── Core Types ───────────────────────────────────────────────────────────────

export interface NotificationPayload {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  /** Optional nav/context metadata. Common keys: link, booking_id, host_id, dispute_id, unit_number */
  data?: Record<string, any>;
  orgId?: string;
}

// ─── Core Insert ──────────────────────────────────────────────────────────────

/**
 * Insert a single in-app notification for one user.
 * Errors are swallowed — notification failures must not block primary actions.
 */
export async function insertNotification(payload: NotificationPayload): Promise<void> {
  try {
    const { error } = await supabase.from("notifications").insert({
      user_id: payload.userId,
      type: payload.type,
      title: payload.title,
      message: payload.message,
      data: payload.data ?? null,
      organization_id: payload.orgId ?? null,
      read: false,
    });
    if (error) {
      console.warn("[notifications] Insert failed:", error.message);
    }
  } catch (e: any) {
    console.warn("[notifications] Insert exception:", e?.message);
  }
}

// ─── Org Lookup Helpers ───────────────────────────────────────────────────────

/**
 * Return user_ids of all property_managers and strata_admins assigned to an org.
 * Used to fan-out notifications to all managers when a host registers, a dispute
 * is filed, etc.
 */
export async function getOrgManagerIds(orgId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from("user_assignments")
      .select("user_id")
      .eq("org_id", orgId)
      .in("role", ["property_manager", "strata_admin"]);

    if (error) {
      console.warn("[notifications] getOrgManagerIds failed:", error.message);
      return [];
    }
    return (data ?? []).map((r) => r.user_id);
  } catch (e: any) {
    console.warn("[notifications] getOrgManagerIds exception:", e?.message);
    return [];
  }
}

/**
 * Send the same notification to every manager in an org (PM + strata_admin).
 * Runs inserts in parallel via Promise.allSettled so one failure doesn't block others.
 */
export async function notifyOrgManagers(
  orgId: string,
  payload: Omit<NotificationPayload, "userId">
): Promise<void> {
  const managerIds = await getOrgManagerIds(orgId);
  if (managerIds.length === 0) return;

  await Promise.allSettled(
    managerIds.map((userId) => insertNotification({ ...payload, userId, orgId }))
  );
}

// ─── Convenience Wrappers ─────────────────────────────────────────────────────

/**
 * Notify a host about their registration status change.
 */
export async function notifyHostRegistrationStatus(
  hostUserId: string,
  approved: boolean,
  unitNumber: string,
  orgId: string,
  reason?: string
): Promise<void> {
  await insertNotification({
    userId: hostUserId,
    type: approved ? "success" : "error",
    title: approved ? "Registration Approved ✓" : "Registration Not Approved",
    message: approved
      ? "Your STR registration has been approved. You can now create bookings."
      : `Your registration was not approved. ${reason ? `Reason: ${reason}` : "Please contact your property manager for details."}`,
    data: { link: "/host-dashboard", unit_number: unitNumber },
    orgId,
  });
}

/**
 * Notify managers about a new host registration awaiting approval.
 */
export async function notifyManagersNewHost(
  orgId: string,
  unitNumber: string,
  hostName: string,
  hostId: string
): Promise<void> {
  await notifyOrgManagers(orgId, {
    type: "info",
    title: "New Host Registration",
    message: `New host registration from Unit ${unitNumber} (${hostName}) awaiting approval.`,
    data: { link: "/hosts", host_id: hostId, unit_number: unitNumber },
  });
}

/**
 * Notify a host about a booking event (approved/blocked).
 */
export async function notifyHostBookingEvent(
  hostUserId: string,
  orgId: string,
  bookingId: string,
  status: "approved" | "blocked",
  guestName: string,
  checkIn: string,
  reason?: string
): Promise<void> {
  await insertNotification({
    userId: hostUserId,
    type: status === "approved" ? "success" : "warning",
    title: status === "approved" ? "Booking Approved ✓" : "Booking Blocked",
    message:
      status === "approved"
        ? `Booking for ${guestName} (check-in ${checkIn}) has been approved. Your guest will receive check-in instructions.`
        : `Booking for ${guestName} was blocked. ${reason ? `Reason: ${reason}` : "Please review the booking details."}`,
    data: { link: "/bookings", booking_id: bookingId },
    orgId,
  });
}

/**
 * Notify managers about a new booking in their org.
 */
export async function notifyManagersNewBooking(
  orgId: string,
  bookingId: string,
  unitNumber: string,
  guestName: string,
  checkIn: string,
  checkOut: string
): Promise<void> {
  await notifyOrgManagers(orgId, {
    type: "info",
    title: "New Booking Created",
    message: `New booking for Unit ${unitNumber}: ${guestName}, ${checkIn} → ${checkOut}.`,
    data: { link: "/bookings", booking_id: bookingId, unit_number: unitNumber },
  });
}

/**
 * Notify managers about a newly filed dispute.
 */
export async function notifyManagersDisputeFiled(
  orgId: string,
  disputeId: string,
  hostName: string,
  unitNumber: string,
  bookingId?: string | null
): Promise<void> {
  await notifyOrgManagers(orgId, {
    type: "warning",
    title: "Dispute Filed",
    message: `Dispute filed by ${hostName} — Unit ${unitNumber}.${bookingId ? ` Regarding booking #${bookingId.slice(0, 8)}.` : ""} Please review.`,
    data: { link: "/disputes", dispute_id: disputeId, unit_number: unitNumber },
  });
}

/**
 * Notify a host that their dispute has been resolved.
 */
export async function notifyHostDisputeResolved(
  hostUserId: string,
  orgId: string,
  disputeId: string,
  resolutionAction?: string | null,
  resolution?: string | null
): Promise<void> {
  const decisionLabel = resolutionAction
    ? resolutionAction.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "See dispute details";

  await insertNotification({
    userId: hostUserId,
    type: "info",
    title: "Dispute Resolved",
    message: `Your dispute has been resolved. Decision: ${decisionLabel}.${resolution ? ` Note: ${resolution}` : ""}`,
    data: { link: "/disputes", dispute_id: disputeId },
    orgId,
  });
}

/**
 * Notify a host that a compliance action has been issued against them.
 */
export async function notifyHostComplianceAction(
  hostUserId: string,
  orgId: string,
  actionType: string,
  description: string,
  unitNumber: string
): Promise<void> {
  const typeLabel = actionType.charAt(0).toUpperCase() + actionType.slice(1);
  await insertNotification({
    userId: hostUserId,
    type: actionType === "penalty" || actionType === "order" ? "error" : "warning",
    title: `Compliance ${typeLabel} Issued`,
    message: `A ${actionType} has been issued for Unit ${unitNumber}. Reason: ${description}`,
    data: { link: "/host-dashboard", unit_number: unitNumber },
    orgId,
  });
}

/**
 * Notify the inviter that their invitation was accepted.
 */
export async function notifyInviterAccepted(
  inviterUserId: string,
  acceptedUserName: string,
  role: string,
  orgId: string | null
): Promise<void> {
  const roleLabel = role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  await insertNotification({
    userId: inviterUserId,
    type: "success",
    title: "Invitation Accepted 🎉",
    message: `${acceptedUserName} has accepted your invitation and joined as ${roleLabel}. Visit My Invitations to see your network.`,
    data: { link: "/my-invitations" },
    orgId: orgId ?? undefined,
  });
}

/**
 * Notify a host about their registration expiry.
 * @param daysUntilExpiry - e.g. 14 or 7
 */
export async function notifyHostRegistrationExpiry(
  hostUserId: string,
  orgId: string,
  unitNumber: string,
  daysUntilExpiry: number
): Promise<void> {
  await insertNotification({
    userId: hostUserId,
    type: "warning",
    title: "Registration Expiring Soon",
    message: `Your STR registration for Unit ${unitNumber} expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? "s" : ""}. Please renew to avoid service interruption.`,
    data: { link: "/my-documents", unit_number: unitNumber },
    orgId,
  });
}

/**
 * Notify managers (and optionally the host) when a compliance threshold is hit
 * (e.g., host has used 80% of annual night limit).
 */
export async function notifyComplianceThreshold(
  orgId: string,
  hostUserId: string,
  unitNumber: string,
  percentUsed: number,
  nightsUsed: number,
  nightLimit: number
): Promise<void> {
  const message = `Unit ${unitNumber} has used ${percentUsed}% of its annual night limit (${nightsUsed}/${nightLimit} nights).`;

  // Notify the host
  await insertNotification({
    userId: hostUserId,
    type: "warning",
    title: `${percentUsed}% Night Limit Reached`,
    message,
    data: { link: "/host-dashboard", unit_number: unitNumber },
    orgId,
  });

  // Notify all managers
  await notifyOrgManagers(orgId, {
    type: "warning",
    title: `Night Limit Alert — Unit ${unitNumber}`,
    message,
    data: { link: "/hosts", unit_number: unitNumber },
  });
}
